import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { operationService } from '../services/operationService';
import { Operation } from '../types';
import { formatApiDateTimeForDisplay } from '../utils/dateTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFilePdf, faTrash, faLock, faLockOpen, faBoxArchive } from '@fortawesome/free-solid-svg-icons';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check permissions
  const canGenerateReports = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-reports'];
  const canManageOperations = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-operations'];
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [asmFilter, setAsmFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [operations, setOperations] = useState<Operation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [deletingOperationId, setDeletingOperationId] = useState<number | null>(null);
  const [togglingOperationId, setTogglingOperationId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    operation: Operation | null;
    isDeleting: boolean;
  }>({
    isOpen: false,
    operation: null,
    isDeleting: false
  });

  const [closeModal, setCloseModal] = useState<{
    isOpen: boolean;
    operation: Operation | null;
    isProcessing: boolean;
  }>({
    isOpen: false,
    operation: null,
    isProcessing: false
  });

  // Archive view toggle (sealed operations = archived)
  const [showArchive, setShowArchive] = useState(false);

  // Check if device is mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchOperations = async () => {
      try {
        setLoading(true);
        setError('');
        
        if (user?.orgID && user.orgID > 0) {
          const data = await operationService.getOperationsByYear([user.orgID], selectedYear);
          setOperations(data);
        } else {
          setError('Keine Organisations-ID verfügbar');
        }
      } catch (error: any) {
        setError(error.message || 'Fehler beim Laden der Einsätze');
        console.error('Error fetching operations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOperations();
  }, [user?.orgID, selectedYear]);

  const archivedCount = operations.filter(op => op.lockStatus === 2).length;

  const filteredOperations = operations.filter((op) => {
    // Sealed operations (lockStatus === 2) go to archive
    if (showArchive && op.lockStatus !== 2) return false;
    if (!showArchive && op.lockStatus === 2) return false;

    const matchesSearch = (op.title || `${op.location}: ${op.alertTitle}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         op.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAsm = asmFilter === 'all' || 
                      (asmFilter === 'yes' && op.asm_teams === true) ||
                      (asmFilter === 'no' && op.asm_teams !== true);
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'open' && op.lockStatus === 0) ||
                         (statusFilter === 'closed' && op.lockStatus === 1);
    
    // In archive view, skip status filter (all are sealed)
    return matchesSearch && matchesAsm && (showArchive || matchesStatus);
  });

  const handleViewOperation = (operation: Operation) => {
    navigate(`/operation/${operation.id}`);
  };



  const handleGenerateReport = async (operation: Operation) => {
    // Check if this is an ASM operation
    if (!operation.asm_teams) {
      alert('PDF Export ist nur für Atemschutzeinsätze verfügbar.');
      return;
    }

    try {
      if (user?.orgID) {
        const blob = await operationService.generateReport(user.orgID, operation.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const einsatzName = operation.title || `${operation.location}: ${operation.alertTitle}` || 'Unbekannter_Einsatz';
        a.download = `Einsatzbericht_${einsatzName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error: any) {
      alert(`Fehler beim Generieren des PDF-Berichts: ${error.message}`);
    }
  };

  const handleDeleteOperation = (operation: Operation) => {
    // Check if smsTxtOrig is filled
    if (operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '') {
      alert('Externe Einsätze (aus einem Fremdsystem) können nicht gelöscht werden.');
      return;
    }

    // Open delete modal
    setDeleteModal({
      isOpen: true,
      operation,
      isDeleting: false
    });
  };

  const confirmDeleteOperation = async () => {
    if (!deleteModal.operation || !user?.orgID) return;

    try {
      setDeleteModal(prev => ({ ...prev, isDeleting: true }));
      setDeletingOperationId(deleteModal.operation.id);
      
      await operationService.deleteOperation(user.orgID, deleteModal.operation.id);
      
      // Remove operation from local state
      setOperations(prev => prev.filter(op => op.id !== deleteModal.operation!.id));
      
      // Close modal after successful deletion
      setDeleteModal({
        isOpen: false,
        operation: null,
        isDeleting: false
      });
      setDeletingOperationId(null);
    } catch (error: any) {
      alert(`Fehler beim Löschen des Einsatzes: ${error.message}`);
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
      setDeletingOperationId(null);
    }
  };

  const cancelDeleteOperation = () => {
    setDeleteModal({
      isOpen: false,
      operation: null,
      isDeleting: false
    });
    setDeletingOperationId(null);
  };

  const handleToggleOperationStatus = async (operation: Operation) => {
    if (!user?.orgID) return;

    if (operation.lockStatus === 1) {
      // Operation is closed, open it
      try {
        setTogglingOperationId(operation.id);
        await operationService.openOperation(user.orgID, operation.id);
        // Update local state
        setOperations(prev => prev.map(op => 
          op.id === operation.id 
            ? { ...op, lockStatus: 0 }
            : op
        ));
      } catch (error: any) {
        alert(`Fehler beim Öffnen des Einsatzes: ${error.message}`);
      } finally {
        setTogglingOperationId(null);
      }
    } else {
      // Operation is open, show close/seal dialog
      setCloseModal({
        isOpen: true,
        operation: operation,
        isProcessing: false
      });
    }
  };

  const handleCloseOperation = async () => {
    if (!closeModal.operation || !user?.orgID) return;

    try {
      setCloseModal(prev => ({ ...prev, isProcessing: true }));
      await operationService.closeOperation(user.orgID, closeModal.operation.id);
      
      // Update local state
      setOperations(prev => prev.map(op => 
        op.id === closeModal.operation!.id 
          ? { ...op, lockStatus: 1 }
          : op
      ));
      
      setCloseModal({ isOpen: false, operation: null, isProcessing: false });
    } catch (error: any) {
      alert(`Fehler beim Schliessen des Einsatzes: ${error.message}`);
      setCloseModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleSealOperation = async () => {
    if (!closeModal.operation || !user?.orgID) return;

    try {
      setCloseModal(prev => ({ ...prev, isProcessing: true }));
      await operationService.sealOperation(user.orgID, closeModal.operation.id);
      
      // Update local state
      setOperations(prev => prev.map(op => 
        op.id === closeModal.operation!.id 
          ? { ...op, lockStatus: 2 }
          : op
      ));
      
      setCloseModal({ isOpen: false, operation: null, isProcessing: false });
    } catch (error: any) {
      alert(`Fehler beim Versiegeln des Einsatzes: ${error.message}`);
      setCloseModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const cancelCloseOperation = () => {
    setCloseModal({ isOpen: false, operation: null, isProcessing: false });
  };

  const formatDate = (dateString: string) => {
    return formatApiDateTimeForDisplay(dateString);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '18px',
        color: '#666'
      }}>
        Einsätze werden geladen...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#ffebee',
        color: '#c91f0c',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '16px'
      }}>
        <strong>Fehler:</strong> {error}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
        <h1 style={{ margin: 0, color: '#333' }}>
          {showArchive ? 'Archiv' : 'Einsätze'}
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowArchive(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: !showArchive ? '#c91f0c' : '#e0e0e0',
              color: !showArchive ? '#fff' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Einsätze ({operations.length - archivedCount})
          </button>
          <button
            onClick={() => setShowArchive(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: showArchive ? '#c91f0c' : '#e0e0e0',
              color: showArchive ? '#fff' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FontAwesomeIcon icon={faBoxArchive} />
            Archiv ({archivedCount})
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
              Suchen
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nach Einsatz suchen..."
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
              Jahr
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
              Atemschutz
            </label>
            <select
              value={asmFilter}
              onChange={(e) => setAsmFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            >
              <option value="all">Alle</option>
              <option value="yes">Mit Atemschutz</option>
              <option value="no">Ohne Atemschutz</option>
            </select>
          </div>
          
          {!showArchive && (
            <div style={{ minWidth: '150px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="all">Alle</option>
                <option value="open">Geöffnet</option>
                <option value="closed">Geschlossen</option>
              </select>
            </div>
          )}
          
          <div style={{ alignSelf: 'end', paddingTop: '25px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              {filteredOperations.length} von {operations.length} Einsätzen
            </span>
          </div>
        </div>
      </div>

      {/* Operations Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              {!isMobile && (
                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd', width: '80px' }}>
                  Nummer
                </th>
              )}
              <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                Titel
              </th>
              {!isMobile && (
                <>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    Kategorie
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    Atemschutz
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    Ort
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    Erstellt
                  </th>
                </>
              )}
              <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredOperations.map((operation) => (
              <tr key={operation.id} style={{ 
                borderBottom: '1px solid #eee',
                backgroundColor: (operation.lockStatus === 1 || operation.lockStatus === 2) ? '#f5f5f5' : 'transparent',
                opacity: (operation.lockStatus === 1 || operation.lockStatus === 2) ? 0.7 : 1
              }}>
                {!isMobile && (
                  <td style={{ padding: '15px', width: '80px' }}>{operation.opsNumber}</td>
                )}
                <td style={{ padding: '15px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {operation.title || `${operation.location}: ${operation.alertTitle}`}
                  </div>
                  {isMobile && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {operation.location}
                    </div>
                  )}
                </td>
                {!isMobile && (
                  <>
                    <td style={{ padding: '15px' }}>{operation.category}</td>
                    <td style={{ padding: '15px' }}>
                      <span style={{
                        backgroundColor: operation.asm_teams ? '#4caf50' : '#c91f0c',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {operation.asm_teams ? 'Ja' : 'Nein'}
                      </span>
                    </td>
                    <td style={{ padding: '15px' }}>{operation.location}</td>
                    <td style={{ padding: '15px' }}>{formatDate(operation.created)}</td>
                  </>
                )}
                <td style={{ padding: '15px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleViewOperation(operation)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#c91f0c', // FireHub primary red
                        color: '#ffffff', // FireHub white
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                      title="Details anzeigen"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    {canGenerateReports && (
                      <button
                        onClick={() => handleGenerateReport(operation)}
                        disabled={!operation.asm_teams}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: operation.asm_teams ? '#c91f0c' : '#666', // Red for enabled, gray for disabled
                          color: '#ffffff', // FireHub white
                          border: 'none',
                          borderRadius: '4px',
                          cursor: operation.asm_teams ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                          opacity: operation.asm_teams ? 1 : 0.7
                        }}
                        title={operation.asm_teams ? "PDF Bericht generieren" : "PDF Export nur für Atemschutzeinsätze verfügbar"}
                      >
                        <FontAwesomeIcon icon={faFilePdf} />
                      </button>
                    )}
                    {canManageOperations && (
                      <button
                        onClick={() => handleToggleOperationStatus(operation)}
                        disabled={togglingOperationId === operation.id || operation.lockStatus === 2}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: togglingOperationId === operation.id 
                            ? '#666' 
                            : operation.lockStatus === 2 
                              ? '#4a148c'  // Dark purple for sealed – clearly distinct
                              : operation.lockStatus === 1 
                                ? '#ff9800'  // Orange for closed
                                : '#4caf50', // Green for open
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: togglingOperationId === operation.id || operation.lockStatus === 2 ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          opacity: togglingOperationId === operation.id ? 0.7 : 1
                        }}
                        title={
                          togglingOperationId === operation.id 
                            ? "Wird geändert..." 
                            : operation.lockStatus === 2
                              ? "Einsatz ist versiegelt (kann nicht bearbeitet werden)"
                              : operation.lockStatus === 1 
                                ? "Einsatz öffnen (wird in App angezeigt)" 
                                : "Einsatz schliessen (wird aus App ausgeblendet)"
                        }
                      >
                        <FontAwesomeIcon icon={operation.lockStatus === 1 ? faLockOpen : faLock} />
                      </button>
                    )}
                    {canManageOperations && !showArchive && (
                      <button
                        onClick={() => handleDeleteOperation(operation)}
                        disabled={deletingOperationId === operation.id || Boolean(operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '') || operation.lockStatus === 2}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: deletingOperationId === operation.id || Boolean(operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '') || operation.lockStatus === 2 ? '#666' : '#d32f2f', // Gray when deleting, has SMS text, or sealed, red when available
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: deletingOperationId === operation.id || Boolean(operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '') || operation.lockStatus === 2 ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          opacity: deletingOperationId === operation.id || Boolean(operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '') || operation.lockStatus === 2 ? 0.7 : 1,
                          textDecoration: operation.lockStatus === 2 ? 'line-through' : 'none'
                        }}
                        title={
                          deletingOperationId === operation.id 
                            ? "Wird gelöscht..." 
                            : operation.lockStatus === 2
                              ? "Versiegelte Einsätze können nicht gelöscht werden"
                              : (operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '')
                                ? "Externe Einsätze (aus einem Fremdsystem) können nicht gelöscht werden." 
                                : "Einsatz löschen"
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOperations.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {showArchive 
            ? (archivedCount === 0 ? 'Keine versiegelten Einsätze im Archiv' : 'Keine archivierten Einsätze gefunden')
            : (operations.length === 0 ? 'Keine Einsätze verfügbar' : 'Keine Einsätze gefunden')
          }
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            minWidth: '400px',
            maxWidth: '500px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <FontAwesomeIcon 
                icon={faTrash} 
                style={{ 
                  fontSize: '24px', 
                  color: '#d32f2f' 
                }} 
              />
            </div>
            
            <h2 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>
              Einsatz löschen
            </h2>
            
            <p style={{ marginBottom: '25px', color: '#666', fontSize: '16px' }}>
              Möchten Sie den Einsatz <strong>"{deleteModal.operation?.title || `${deleteModal.operation?.location}: ${deleteModal.operation?.alertTitle}` || 'Unbekannter Einsatz'}"</strong> wirklich löschen?
            </p>
            
            <p style={{ marginBottom: '25px', color: '#999', fontSize: '14px' }}>
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={cancelDeleteOperation}
                disabled={deleteModal.isDeleting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#666',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: deleteModal.isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: deleteModal.isDeleting ? 0.7 : 1
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDeleteOperation}
                disabled={deleteModal.isDeleting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#d32f2f',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: deleteModal.isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: deleteModal.isDeleting ? 0.7 : 1
                }}
              >
                {deleteModal.isDeleting ? 'Wird gelöscht...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close/Seal Modal */}
      {closeModal.isOpen && closeModal.operation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ 
              marginBottom: '20px', 
              color: '#333', 
              fontSize: '20px',
              textAlign: 'center'
            }}>
              Einsatz schliessen
            </h2>
            
            <p style={{ marginBottom: '25px', color: '#666', fontSize: '16px', textAlign: 'center' }}>
              Wie möchtest Du den Einsatz <strong>"{closeModal.operation.title || `${closeModal.operation.location}: ${closeModal.operation.alertTitle}` || 'Unbekannter Einsatz'}"</strong> behandeln?
            </p>

            <div style={{ marginBottom: '25px' }}>
              <div style={{
                padding: '15px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                marginBottom: '15px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '16px' }}>🔒 Schliessen</h3>
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                  Einsatz wird geschlossen, kann aber später noch bearbeitet werden.
                </p>
              </div>
              
              <div style={{
                padding: '15px',
                border: '2px solid #ffc107',
                borderRadius: '8px',
                backgroundColor: '#fffbf0',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '16px' }}>🔐 Versiegeln</h3>
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                  Einsatz wird dauerhaft versiegelt, kann nicht mehr bearbeitet werden und wird ins Archiv verschoben.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={cancelCloseOperation}
                disabled={closeModal.isProcessing}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#666',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: closeModal.isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: closeModal.isProcessing ? 0.7 : 1
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleCloseOperation}
                disabled={closeModal.isProcessing}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4caf50',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: closeModal.isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: closeModal.isProcessing ? 0.7 : 1
                }}
              >
                {closeModal.isProcessing ? 'Wird geschlossen...' : 'Schliessen'}
              </button>
              <button
                onClick={handleSealOperation}
                disabled={closeModal.isProcessing}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ffc107',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: closeModal.isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: closeModal.isProcessing ? 0.7 : 1
                }}
              >
                {closeModal.isProcessing ? 'Wird versiegelt...' : 'Versiegeln'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 